import { datadogRum } from '@datadog/browser-rum'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { datadogLogs } from '@datadog/browser-logs'
import { ErrorBoundary } from 'react-error-boundary'
import 'fix-date'
// Design System 2
import './ui_for_v2.scss'
import './react_datepicker.css'
import './ui.css'

import { createReduxStore } from './store'
import App from './App'
import { checkLoggedIn, PreloadedState } from './utils/session'
import ErrorBoundaryScreen from './v2-components/ErrorBoundaryScreen'
import { initOpenFeature } from '@/flags'
import { logHeardError } from './utils/errorHelpers'

if (
  process.env.VITE_ENV &&
  ['staging', 'production'].includes(process.env.VITE_ENV)
) {
  datadogRum.init({
    applicationId: '1258bcd3-1596-4b1f-bc97-b196156c8a6b',
    clientToken: 'pub3d876dba58507e63fe5533a95e7fa67c',
    site: 'datadoghq.com',
    service: 'heard-app',
    env: process.env.VITE_ENV,
    version: process.env.VITE_SHORT_SHA,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  })

  datadogLogs.init({
    clientToken: 'pub1c5a93fb02e7bdaa6f097da69f8e59b4',
    site: 'datadoghq.com',
    service: 'app',
    env: process.env.VITE_ENV,
    version: process.env.VITE_SHORT_SHA,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  })
}

datadogRum.startSessionReplayRecording()

const renderApp = (preloadedState: PreloadedState) => {
  const store = createReduxStore(preloadedState)

  const root = createRoot(document.getElementById('root') as HTMLElement)
  root.render(
    <ErrorBoundary fallback={<ErrorBoundaryScreen />} onError={logHeardError}>
      <Provider store={store}>
        <App />
      </Provider>
    </ErrorBoundary>
  )
}

;(async () => {
  let preloadedState

  try {
    // Initialize feature flags and wait for them to be ready. Otherwise,
    // feature flag availability is subject to race conditions.
    await initOpenFeature()
  } catch (e) {
    logHeardError(e)
  }

  try {
    // This call will always succeed as long as api is up
    preloadedState = await checkLoggedIn()
  } catch (e) {
    // Log error and display message if api is down
    logHeardError(e)
    const root = createRoot(document.getElementById('root') as HTMLElement)

    return root.render(<ErrorBoundaryScreen />)
  }

  return renderApp(preloadedState)
})()
